/**
 * @file useJobStream.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-01
 * @modified 2026-10-19
 *
 * Follow a server job (services/jobService) over its SSE stream,
 * GET /api/jobs/:id/stream, and survive the connection dropping.
 *
 *   const { status, progress, result, error } = useJobStream(jobId, {
 *     onProgress, onComplete, onError, onLocalDone, onVerdictsDone, fetchPersisted
 *   });
 *
 * status: idle | connecting | running | reconnecting | completed | failed | lost
 *
 * The stream replays the job's current state on every connect, so a reconnect
 * loses nothing. When the connection errors before the job is done the hook
 * closes it (the browser's own retry would hammer a dead job forever) and asks
 * GET /api/jobs/:id what happened:
 *   - 404: the job is gone (a restart, or the hour's GC). fetchPersisted() is
 *     asked for the saved result: a value means completed, nothing means lost.
 *   - finished: the JSON carries the result or the error — use it.
 *   - still running: reopen after 1, 2, 4, 8, 15 s …, at most 8 tries.
 *
 * onLocalDone / onVerdictsDone fire once each, on the first progress event
 * carrying that flag (the statement integrity check's phases). Callbacks are
 * read through a ref, so passing fresh closures every render never reopens
 * the stream.
 */

import { useEffect, useRef, useState } from 'react';
import { API_URL } from '../config';

const BACKOFF_SECONDS = [1, 2, 4, 8, 15];
const MAX_TRIES = 8;

const IDLE = { status: 'idle', progress: null, result: null, error: null };

export default function useJobStream(jobId, options = {}) {
  const [state, setState] = useState(IDLE);
  const optionsRef = useRef(options);
  useEffect(() => { optionsRef.current = options; });

  // Per job, so React's double effect in development cannot fire a callback twice.
  const firedRef = useRef({ jobId: null, local: false, verdicts: false, terminal: false });

  useEffect(() => {
    if (!jobId) {
      setState(IDLE);
      return undefined;
    }
    if (firedRef.current.jobId !== jobId) {
      firedRef.current = { jobId, local: false, verdicts: false, terminal: false };
    }

    let cancelled = false;
    let done = false;
    let source = null;
    let timer = null;
    let tries = 0;

    setState({ status: 'connecting', progress: null, result: null, error: null });

    const fire = (name, ...args) => {
      const fn = optionsRef.current?.[name];
      if (typeof fn !== 'function') return;
      try { fn(...args); } catch (e) { console.error(`useJobStream ${name} handler failed:`, e); }
    };

    const close = () => {
      if (!source) return;
      source.onmessage = null;
      source.onerror = null;
      source.close();
      source = null;
    };

    const onProgress = (progress) => {
      if (!progress || cancelled) return;
      setState((s) => ({ ...s, status: 'running', progress }));
      fire('onProgress', progress);
      const f = firedRef.current;
      if (progress.localDone && !f.local) { f.local = true; fire('onLocalDone', progress); }
      if (progress.verdictsDone && !f.verdicts) { f.verdicts = true; fire('onVerdictsDone', progress); }
    };

    const finishOk = (result) => {
      if (cancelled || done) return;
      done = true;
      close();
      setState((s) => ({ ...s, status: 'completed', result: result ?? null, error: null }));
      if (!firedRef.current.terminal) { firedRef.current.terminal = true; fire('onComplete', result ?? null); }
    };

    const finishErr = (message, status = 'failed') => {
      if (cancelled || done) return;
      done = true;
      close();
      setState((s) => ({ ...s, status, error: message }));
      if (!firedRef.current.terminal) { firedRef.current.terminal = true; fire('onError', message, status); }
    };

    const scheduleReopen = () => {
      if (tries >= MAX_TRIES) {
        finishErr('Lost the connection to the server — the job may still be running there', 'lost');
        return;
      }
      const wait = BACKOFF_SECONDS[Math.min(tries, BACKOFF_SECONDS.length - 1)] * 1000;
      tries += 1;
      setState((s) => ({ ...s, status: 'reconnecting' }));
      timer = setTimeout(open, wait);
    };

    const recover = async () => {
      setState((s) => ({ ...s, status: 'reconnecting' }));
      let res = null;
      try {
        res = await fetch(`${API_URL}/api/jobs/${encodeURIComponent(jobId)}`, { credentials: 'include' });
      } catch (_) {
        res = null; // network down: treat as "still running" and back off
      }
      if (cancelled || done) return;

      if (res && res.status === 404) {
        let persisted = null;
        const fetchPersisted = optionsRef.current?.fetchPersisted;
        if (typeof fetchPersisted === 'function') {
          try { persisted = await fetchPersisted(); } catch (_) { persisted = null; }
        }
        if (cancelled || done) return;
        if (persisted) finishOk(persisted);
        else finishErr('The job is no longer on the server (it may have restarted)', 'lost');
        return;
      }
      if (res && (res.status === 401 || res.status === 403)) {
        finishErr(res.status === 403 ? 'This job belongs to someone else' : 'You have been signed out', 'lost');
        return;
      }
      if (res && res.ok) {
        let job = null;
        try { job = await res.json(); } catch (_) { job = null; }
        if (cancelled || done) return;
        if (job?.progress) onProgress(job.progress);
        if (job?.status === 'completed') { finishOk(job.result); return; }
        if (job?.status === 'failed') { finishErr(job.error || 'The job failed'); return; }
      }
      scheduleReopen();
    };

    function open() {
      if (cancelled || done) return;
      close();
      try {
        source = new EventSource(`${API_URL}/api/jobs/${encodeURIComponent(jobId)}/stream`, { withCredentials: true });
      } catch (e) {
        scheduleReopen();
        return;
      }
      source.onmessage = (ev) => {
        let data;
        try { data = JSON.parse(ev.data); } catch (_) { return; }
        tries = 0;
        switch (data?.type) {
          case 'progress': onProgress(data.progress); break;
          case 'complete': finishOk(data.result); break;
          case 'error': finishErr(data.error || 'The job failed'); break;
          default: break; // heartbeat
        }
      };
      source.onerror = () => {
        if (cancelled || done) return;
        close();
        recover();
      };
    }

    open();

    return () => {
      cancelled = true;
      clearTimeout(timer);
      close();
    };
  }, [jobId]);

  return state;
}
